import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import Spinner from './Spinner';

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-full text-sm font-medium transition-colors cursor-pointer  focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-white hover:bg-white hover:border-primary hover:text-primary border rounded-full',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'flex w-full  m-1  border border-primary px-2 py-2 font-bold text-primary transition-all duration-150 ease-linear focus:outline-hidden',
        secondary: 'bg-secondary text-primary',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary  underline-offset-4 hover:underline',
        Error:
          'text-[#D62550] me-2 flex w-full items-center justify-center   border border-[#D62550] px-2 py-2 font-medium  hover:bg-[#D62550]  hover:text-white',
        defaultPik: 'text-black bg-[#E7E7E8]  hover:text-white hover:bg-primary',
      },

      size: {
        default: 'h-12 px-4 py-2 w-full',
        sm: 'h-9  px-3',
        lg: 'h-11  px-8',
        icon: 'h-10 w-10',
      },
      rounded: {
        default: 'rounded-md',
        full: 'rounded-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      rounded: 'full',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant, size, rounded, asChild = false, loading = false, className, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={clsx(
          buttonVariants({ variant, size, rounded, className }),
          !!loading && 'pointer-events-none',
        )}
        ref={ref}
        disabled={!!loading}
        {...props}
      >
        {loading ? <Spinner size="small" /> : props.children}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
